import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Gavel, LayoutDashboard, Target, Clock, Plus, CalendarDays, Crown, LogIn, ShieldCheck, Menu, Info, HelpCircle, Mail, ChevronDown, BookOpen, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { BrandGavel } from "./BrandGavel";
import TrialRibbon from "./TrialRibbon";
import SettingsDialog from "./SettingsDialog";
import AuthDialog from "./AuthDialog";
import InstallPrompt from "./InstallPrompt";
import PendingRecordingSync from "./PendingRecordingSync";
import MinutesReadyPing from "./MinutesReadyPing";
import { useReportTimezone } from "../lib/timezone";
import { useAuth } from "../lib/auth";
import StartSessionPrompt from "./StartSessionPrompt";

const navItems = [
  { to: "/", label: "Boardroom", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/meetings", label: "Sessions", icon: Clock, testId: "nav-meetings" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar" },
  { to: "/board-books", label: "Board Books", icon: BookOpen, testId: "nav-board-books" },
  { to: "/goals", label: "Goals", icon: Target, testId: "nav-goals" },
];

const infoNavItems = [
  { to: "/milestones", label: "Milestones", icon: Award, testId: "nav-milestones" },
  { to: "/pricing", label: "Pricing", icon: Crown, testId: "nav-pricing" },
  { to: "/about", label: "About", icon: Info, testId: "nav-about" },
  { to: "/faq", label: "FAQ", icon: HelpCircle, testId: "nav-faq" },
  { to: "/contact", label: "Contact", icon: Mail, testId: "nav-contact" },
];

export default function AppShell() {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authView, setAuthView] = useState("login");

  // Any screen can ask for the auth dialog (e.g. the landing page CTA), and
  // can ask for the sign-up view via `detail: { view: "signup" }`.
  useEffect(() => {
    const open = (e) => {
      setAuthView(e?.detail?.view === "signup" ? "signup" : "login");
      setAuthOpen(true);
    };
    window.addEventListener("boardroom:open-auth", open);
    return () => window.removeEventListener("boardroom:open-auth", open);
  }, []);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { user, loading } = useAuth();

  // So Monday nudges and reminders land at the chair's own 8am. Re-reported
  // when the signed-in identity changes.
  useReportTimezone(user?.id || user?.email || null);
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa] overflow-x-hidden">
      <header
        className="glass sticky top-0 z-30 border-b border-white/5"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-2">
          {/* Brand — always leftmost. */}
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="brand-link">
            <div className="h-11 w-11 sm:h-9 sm:w-9 rounded-md border border-[#2a2a2a] bg-[#0f0d09] flex items-center justify-center group-hover:border-[#c89f45] transition-colors">
              <BrandGavel size={22} />
            </div>
            <div className="hidden sm:flex flex-col leading-none min-w-0">
              <span className="font-serif text-base tracking-tight">Boardroom</span>
              <span className="label-eyebrow mt-1 hidden 2xl:block whitespace-nowrap">Personal accountability</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-0.5 flex-1 min-w-0 justify-center">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  title={item.label}
                  className={`px-2.5 xl:px-3.5 py-2 rounded-md text-sm transition-colors duration-200 flex items-center gap-2 whitespace-nowrap ${
                    active
                      ? "bg-[#121212] text-[#fafafa] border border-[#2a2a2a]"
                      : "text-[#888] hover:text-white hover:bg-[#121212]"
                  }`}
                >
                  <Icon size={14} />
                  <span className="hidden xl:inline">{item.label}</span>
                </NavLink>
              );
            })}

            {/* Desktop "Learn" dropdown — Pricing / About / FAQ / Contact live
                here to keep the primary nav uncluttered on smaller laptops. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="nav-learn-trigger"
                  className={`px-2.5 xl:px-3.5 py-2 rounded-md text-sm transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap ${
                    infoNavItems.some((i) => location.pathname === i.to)
                      ? "bg-[#121212] text-[#fafafa] border border-[#2a2a2a]"
                      : "text-[#888] hover:text-white hover:bg-[#121212]"
                  }`}
                >
                  <BookOpen size={14} className="xl:hidden" />
                  <span className="hidden xl:inline">More</span>
                  <ChevronDown size={14} className="text-[#666]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#eaeaea] min-w-[180px]"
              >
                {infoNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.to} asChild>
                      <NavLink
                        to={item.to}
                        data-testid={`desktop-${item.testId}`}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#121212] focus:bg-[#121212] rounded-sm"
                      >
                        <Icon size={14} className="text-[#c89f45]" />
                        {item.label}
                      </NavLink>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* CTA cluster — pinned right, compresses before it can clip. */}
          <div className="flex items-center gap-1.5 shrink-0 ml-auto md:ml-0">
          <TrialRibbon />
          <StartSessionPrompt
            testId="header-new-session"
            className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md h-11 sm:h-9 px-3.5 sm:px-3 transition-colors shrink-0 flex items-center gap-1.5"
          >
            <Plus size={14} />
            <span className="hidden xl:inline">New session</span>
            <span className="xl:hidden">New</span>
          </StartSessionPrompt>
          {!loading && !user && (
            <Button
              onClick={() => { setAuthView("login"); setAuthOpen(true); }}
              variant="outline"
              className="h-11 sm:h-9 px-3 shrink-0 bg-transparent border-[#2a2a2a] text-[#ddd] hover:text-[#c89f45] hover:border-[#c89f45] rounded-md font-medium flex items-center gap-1.5"
              data-testid="header-sign-in"
            >
              <LogIn size={14} />
              <span>Sign in</span>
            </Button>
          )}
          {!loading && user && (
            <>
              {user.is_admin && (
                <Link
                  to="/admin/orphans"
                  className={`ml-1 sm:ml-2 h-11 sm:h-9 flex items-center gap-1.5 px-2.5 rounded-md border text-xs font-mono uppercase tracking-widest transition-colors ${
                    location.pathname.startsWith("/admin")
                      ? "border-[#c89f45] text-[#c89f45] bg-[#c89f45]/5"
                      : "border-[#2a2a2a] text-[#888] hover:text-[#c89f45] hover:border-[#c89f45]/60"
                  }`}
                  title="Admin console"
                  data-testid="header-admin-link"
                >
                  <ShieldCheck size={13} />
                  <span className="hidden 2xl:inline">Admin</span>
                </Link>
              )}
              <button
                onClick={() => setSettingsOpen(true)}
                className="ml-1 sm:ml-2 h-11 sm:h-9 pl-1.5 pr-1.5 sm:pr-3 rounded-md border border-[#2a2a2a] shrink-0 hover:border-[#c89f45] transition-colors flex items-center gap-2 text-xs text-[#ddd]"
                title={user.email}
                data-testid="header-user-chip"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-7 w-7 rounded-sm object-cover" />
                ) : (
                  <span className="h-7 w-7 rounded-sm bg-[#121212] flex items-center justify-center text-[#c89f45] font-serif text-sm">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="hidden 2xl:inline max-w-[110px] truncate">{user.name || user.email}</span>
              </button>
            </>
          )}
          </div>

          {/* Mobile menu trigger — on the RIGHT, since that's where the
              user's thumb reaches on modern phones. Hidden on md+. */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="md:hidden h-11 w-11 shrink-0 flex items-center justify-center rounded-md border border-[#2a2a2a] bg-[#121212] hover:border-[#c89f45] transition-colors ml-1"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu size={16} className="text-[#eaeaea]" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#0a0a0a] border-l border-[#1a1a1a] text-[#fafafa] p-0 flex flex-col"
              style={{
                paddingTop: "env(safe-area-inset-top)",
                paddingBottom: "env(safe-area-inset-bottom)",
              }}
              data-testid="mobile-menu-sheet"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a1a1a]">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-md border border-[#2a2a2a] bg-[#0f0d09] flex items-center justify-center">
                    <BrandGavel size={22} />
                  </div>
                  <div className="flex flex-col leading-none min-w-0">
                    <span className="font-serif text-base tracking-tight">Boardroom</span>
                    <span className="label-eyebrow mt-1 whitespace-nowrap">Personal OS</span>
                  </div>
                </div>
              </div>
              {/* Mobile trial ribbon lives in the sheet header since the
                  main header pill is hidden below sm:. Force-visible via
                  wrapper class that overrides the sm-only hide. */}
              <div className="px-5 pt-3 [&_[data-testid=trial-ribbon]]:!inline-flex" onClick={() => setMobileNavOpen(false)}>
                <TrialRibbon />
              </div>
              <nav className="flex flex-col gap-1 p-3 overflow-y-auto">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileNavOpen(false)}
                      data-testid={`mobile-${item.testId}`}
                      className={`px-4 py-3 rounded-md text-sm transition-colors flex items-center gap-3 ${
                        active
                          ? "bg-[#121212] text-[#fafafa] border border-[#2a2a2a]"
                          : "text-[#bbb] hover:text-white hover:bg-[#121212]"
                      }`}
                    >
                      <Icon size={16} className={active ? "text-[#c89f45]" : ""} />
                      {item.label}
                    </NavLink>
                  );
                })}

                <div className="mt-4 mb-2 px-4 label-eyebrow">Learn</div>
                {infoNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname === item.to;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileNavOpen(false)}
                      data-testid={`mobile-${item.testId}`}
                      className={`px-4 py-3 rounded-md text-sm transition-colors flex items-center gap-3 ${
                        active
                          ? "bg-[#121212] text-[#fafafa] border border-[#2a2a2a]"
                          : "text-[#bbb] hover:text-white hover:bg-[#121212]"
                      }`}
                    >
                      <Icon size={16} className={active ? "text-[#c89f45]" : ""} />
                      {item.label}
                    </NavLink>
                  );
                })}
              </nav>
              <div className="px-3 pb-4">
                <div className="mt-4 mb-2 px-4 label-eyebrow">Account</div>
                {!loading && user ? (
                  <button
                    type="button"
                    onClick={() => { setMobileNavOpen(false); setSettingsOpen(true); }}
                    className="w-full text-left px-4 py-3 rounded-md text-[#bbb] hover:text-white hover:bg-[#121212] flex items-center gap-3 text-sm"
                    data-testid="mobile-nav-settings"
                  >
                    <ShieldCheck size={16} />
                    <span className="truncate">Settings · {user.name || user.email}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setMobileNavOpen(false); setAuthView("login"); setAuthOpen(true); }}
                    className="w-full text-left px-4 py-3 rounded-md text-[#c89f45] hover:bg-[#121212] flex items-center gap-3 text-sm"
                    data-testid="mobile-nav-sign-in"
                  >
                    <LogIn size={16} />
                    Sign in / create account
                  </button>
                )}
              </div>
              <div className="mt-auto px-5 py-4 border-t border-[#1a1a1a] text-[10px] font-mono uppercase tracking-widest text-[#6a6a6a]">
                Session integrity is everything
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <Outlet />
      </main>

      <footer className="border-t border-white/5 mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 text-xs text-[#8a8a8a]">
          <span className="font-mono">BOARDROOM // PERSONAL OS</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-0 [&_a]:min-h-[40px] [&_a]:inline-flex [&_a]:items-center [&_a]:px-1">
            <Link to="/about" className="hover:text-[#c89f45] transition-colors" data-testid="footer-about-link">
              About
            </Link>
            <Link to="/faq" className="hover:text-[#c89f45] transition-colors" data-testid="footer-faq-link">
              FAQ
            </Link>
            <Link to="/contact" className="hover:text-[#c89f45] transition-colors" data-testid="footer-contact-link">
              Contact
            </Link>
            <Link to="/founders" className="hover:text-[#c89f45] transition-colors" data-testid="footer-founders-link">
              Founders wall
            </Link>
            <span className="label-eyebrow">Session integrity is everything</span>
          </div>
        </div>
      </footer>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <AuthDialog key={authView} open={authOpen} onOpenChange={setAuthOpen} initialView={authView} />
      <PendingRecordingSync />
      <MinutesReadyPing />
      <InstallPrompt />
    </div>
  );
}
